import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalAmount",
    "remainingAmount",
    "remainingAmountInline",
    "paymentsList",
    "paymentsSection",
    "validateButton",
    "ticketItemsList",
    "clientCredits",
    "standardPaymentSection",
    "amountForm",
    "amountInput",
    "remainingAmountButton",
    "cashChangeSection",
    "cashReceivedInput",
    "selectedMethodName"
  ]

  static values = {
    ticketId: Number,
    ticketTotal: Number,
    isRefund: { type: Boolean, default: false },
    ticketItems: Array,
    clientSubscriptions: Array,
    clientCredits: Array,
    hasSecondSessionPromo: { type: Boolean, default: false }
  }

  connect() {
    this.provisionalPayments = []
    this.itemPaymentAssignments = {} // { ticketItemId: { subscriptionId: ..., amount: ... } }
    this.selectedPaymentMethod = null
    
    this.initializeItemAssignments()
    this.renderTicketItems()
    this.renderClientCredits()
    this.updateDisplay()
  }

  initializeItemAssignments() {
    // Initialiser les assignations pour chaque article
    this.ticketItemsValue.forEach(item => {
      if (item.is_parent && item.children) {
        // Pour les parents avec enfants, initialiser les assignations pour chaque enfant
        item.children.forEach(child => {
          this.itemPaymentAssignments[child.id] = {
            subscriptionId: null,
            subscriptionData: null,
            creditCategory: null,
            amount: 0,
            covered: false
          }
        })
        // Initialiser aussi le parent (mais il ne sera pas directement assignable)
        this.itemPaymentAssignments[item.id] = {
          subscriptionId: null,
          subscriptionData: null,
          creditCategory: null,
          amount: 0,
          covered: false
        }
      } else {
        this.itemPaymentAssignments[item.id] = {
          subscriptionId: null,
          subscriptionData: null,
          creditCategory: null,
          amount: 0,
          covered: false
        }
      }
    })
  }

  // ============================================
  // RENDU DES ARTICLES DU TICKET
  // ============================================

  renderTicketItems() {
    if (!this.hasTicketItemsListTarget) return

    let html = ""
    
    this.ticketItemsValue.forEach(item => {
      if (item.is_parent && item.children && item.children.length > 0) {
        // Séance multi-soins : afficher le parent puis les enfants
        html += this.renderParentItemRow(item)
        item.children.forEach(child => {
          html += this.renderChildItemRow(child, item)
        })
      } else {
        // Article simple
        html += this.renderTicketItemRow(item)
      }
    })

    if (html === "") {
      html = '<p class="text-gray-500 text-sm text-center py-4">Aucun article</p>'
    }

    this.ticketItemsListTarget.innerHTML = html
  }

  renderParentItemRow(item) {
    // Vérifier si tous les enfants sont couverts
    const allChildrenCovered = item.children.every(child => {
      const assignment = this.itemPaymentAssignments[child.id]
      return assignment?.covered || false
    })
    
    const rowBgClass = allChildrenCovered ? 'bg-green-50' : 'bg-gray-100'
    const coveredBadge = allChildrenCovered ? `
      <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        ✓ Payé
      </span>
    ` : ''

    return `
      <div class="px-4 py-3 ${rowBgClass} border-b-2 border-gray-200" data-item-id="${item.id}" data-is-parent="true">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center">
              <span class="font-semibold text-gray-900">📋 ${item.description}</span>
              <span class="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Séance multi-soins</span>
              ${coveredBadge}
            </div>
            <p class="text-xs text-gray-500 mt-1">${item.children.length} soin(s) - Choisissez un abonnement pour chaque soin ci-dessous</p>
          </div>
          <div class="text-right">
            <p class="font-semibold ${allChildrenCovered ? 'text-gray-400 line-through' : 'text-gray-900'}">${this.formatCurrency(item.total_price)}</p>
          </div>
        </div>
      </div>
    `
  }

  renderChildItemRow(child, parentItem) {
    const assignment = this.itemPaymentAssignments[child.id]
    const compatibleSubscriptions = this.getCompatibleSubscriptions(child)
    const isCovered = assignment?.covered || false
    
    const rowBgClass = isCovered ? 'bg-green-50' : 'bg-white'
    const priceClass = isCovered ? 'text-gray-400 line-through' : 'text-gray-700'
    const coveredBadge = isCovered ? `
      <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        ✓ Payé
      </span>
    ` : ''

    // Construire le sélecteur d'abonnement sous forme de boutons
    let subscriptionSelector = this.renderSubscriptionButtons(child, compatibleSubscriptions, assignment, parentItem.id, true)

    return `
      <div class="pl-8 pr-4 py-2 ${rowBgClass} border-l-4 border-blue-200 hover:bg-gray-50 transition-colors" data-item-id="${child.id}" data-is-child="true" data-parent-id="${parentItem.id}">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center">
              <span class="text-gray-400 mr-2">↳</span>
              <span class="font-medium text-gray-700">${child.description}</span>
              ${coveredBadge}
            </div>
            ${subscriptionSelector}
          </div>
          <div class="text-right ml-4">
            <p class="font-medium ${priceClass}">${this.formatCurrency(child.total_price)}</p>
            ${isCovered && assignment?.subscriptionData ? `
              <p class="text-xs text-green-600">${assignment.subscriptionData.plan_name}</p>
            ` : ''}
            ${isCovered && assignment?.creditCategory ? `
              <p class="text-xs text-purple-600">Crédit ${this.getCategoryLabel(assignment.creditCategory)}</p>
            ` : ''}
          </div>
        </div>
      </div>
    `
  }

  renderSubscriptionButtons(item, compatibleSubscriptions, assignment, parentId = null, isChild = false) {
    const currentSubscriptionId = assignment?.subscriptionId || ''
    const currentCreditCategory = assignment?.creditCategory || ''
    const dataAttributes = isChild 
      ? `data-item-id="${item.id}" data-parent-id="${parentId}" data-is-child="true"`
      : `data-item-id="${item.id}"`
    
    // Trouver les crédits compatibles pour cet item
    const compatibleCredits = this.getCompatibleCredits(item)
    
    // Déterminer si "Comptant" est sélectionné (ni abonnement ni crédit)
    const cashSelected = !currentSubscriptionId && !currentCreditCategory
    const cashButtonClass = cashSelected 
      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
    
    // Cas 1 : Produit ou service sans type - afficher uniquement comptant + crédits compatibles
    if (!item.service_type) {
      // Pas de type de service = paiement comptant uniquement
      return `
        <div class="mt-2">
          <span class="inline-flex items-center text-xs text-gray-500">
            <span class="mr-1">💳</span> Paiement comptant
          </span>
        </div>`
    }
    
    // Construire les boutons d'options de paiement
    let html = `
      <div class="mt-3 space-y-2">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">Mode de paiement</p>
        <div class="flex flex-wrap gap-2">
          <button type="button"
                  class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border-2 transition-all ${cashButtonClass}"
                  data-action="click->payment-modal#onCashButtonClick"
                  ${dataAttributes}>
            <span class="mr-1.5">💳</span>
            <span class="${cashSelected ? 'text-blue-700' : 'text-gray-700'}">Comptant</span>
          </button>`
    
    // Boutons d'abonnements compatibles
    compatibleSubscriptions.forEach(sub => {
      const isSelected = currentSubscriptionId === sub.id || currentSubscriptionId === sub.id?.toString()
      const buttonClass = isSelected 
        ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      
      const balance = sub.mode === 'credit' 
        ? `${this.formatCurrency(this.getRemainingSubscriptionCredit(sub))}`
        : `${this.getRemainingSubscriptionSessions(sub)} séance(s)`
      
      const icon = sub.mode === 'credit' ? '💰' : '🎟️'
      const textColor = isSelected ? 'text-green-700' : 'text-gray-700'
      const balanceColor = isSelected ? 'text-green-600' : 'text-gray-500'
      
      html += `
        <button type="button"
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border-2 transition-all ${buttonClass}"
                data-action="click->payment-modal#onSubscriptionButtonClick"
                ${dataAttributes}
                data-subscription-id="${sub.id}">
          <span class="mr-1.5">${icon}</span>
          <span class="${textColor}">${sub.plan_name}</span>
          <span class="ml-2 text-xs ${balanceColor}">(${balance})</span>
        </button>`
    })
    
    // Boutons de crédits compatibles
    compatibleCredits.forEach(credit => {
      const isSelected = currentCreditCategory === credit.category
      const buttonClass = isSelected 
        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      
      const remainingCredit = this.getRemainingCreditBalance(credit.category)
      const textColor = isSelected ? 'text-purple-700' : 'text-gray-700'
      const balanceColor = isSelected ? 'text-purple-600' : 'text-gray-500'
      
      html += `
        <button type="button"
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border-2 transition-all ${buttonClass}"
                data-action="click->payment-modal#onCreditButtonClick"
                ${dataAttributes}
                data-credit-category="${credit.category}">
          <span class="mr-1.5">💎</span>
          <span class="${textColor}">Crédit ${this.getCategoryLabel(credit.category)}</span>
          <span class="ml-2 text-xs ${balanceColor}">(${this.formatCurrency(remainingCredit)})</span>
        </button>`
    })
    
    // Si aucun abonnement ni crédit compatible, afficher un message
    if (compatibleSubscriptions.length === 0 && compatibleCredits.length === 0) {
      html += `
        <span class="inline-flex items-center text-xs text-gray-400 ml-2">
          (pas d'abonnement ou crédit compatible)
        </span>`
    }
    
    html += `
        </div>
      </div>`
    
    return html
  }
  
  // Trouver les crédits compatibles avec un item selon son service_type
  getCompatibleCredits(item) {
    const credits = this.clientCreditsValue || []
    
    return credits.filter(credit => {
      // Le crédit "general" est compatible avec tout
      if (credit.category === 'general') {
        return this.getRemainingCreditBalance(credit.category) > 0
      }
      
      // Le crédit doit correspondre au service_type de l'item
      if (credit.category === item.service_type) {
        return this.getRemainingCreditBalance(credit.category) > 0
      }
      
      return false
    })
  }
  
  // Calculer le solde restant d'un crédit (après déduction des assignations)
  getRemainingCreditBalance(category) {
    const credit = (this.clientCreditsValue || []).find(c => c.category === category)
    if (!credit) return 0
    
    const baseBalance = credit.balance || 0
    
    // Soustraire ce qui a déjà été assigné à d'autres articles
    const usedInAssignments = Object.values(this.itemPaymentAssignments)
      .filter(a => a.creditCategory === category)
      .reduce((sum, a) => sum + (a.amount || 0), 0)
    
    return Math.max(0, baseBalance - usedInAssignments)
  }

  renderTicketItemRow(item) {
    const assignment = this.itemPaymentAssignments[item.id]
    const compatibleSubscriptions = this.getCompatibleSubscriptions(item)
    const isCovered = assignment?.covered || false
    const isSubscriptionPlan = item.is_subscription_plan
    
    // Style conditionnel si l'article est couvert
    const rowBgClass = isCovered ? 'bg-green-50' : 'bg-white'
    const priceClass = isCovered ? 'text-gray-400 line-through' : 'text-gray-900'
    const coveredBadge = isCovered ? `
      <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        ✓ Payé
      </span>
    ` : ''

    // Les abonnements achetés ne peuvent pas être payés par d'autres abonnements
    if (isSubscriptionPlan) {
      return `
        <div class="px-4 py-3 ${rowBgClass}" data-item-id="${item.id}">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center">
                <span class="font-medium text-gray-900">${item.description}</span>
                <span class="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded">Abonnement</span>
                ${coveredBadge}
              </div>
              <p class="text-xs text-gray-500 mt-1">×${item.quantity}</p>
            </div>
            <div class="text-right">
              <p class="font-semibold ${priceClass}">${this.formatCurrency(item.total_price)}</p>
              <p class="text-xs text-gray-500">À régler en paiement standard</p>
            </div>
          </div>
        </div>
      `
    }

    // Construire le sélecteur d'abonnement sous forme de boutons
    let subscriptionSelector = this.renderSubscriptionButtons(item, compatibleSubscriptions, assignment, null, false)

    return `
      <div class="px-4 py-3 ${rowBgClass} hover:bg-gray-50 transition-colors" data-item-id="${item.id}">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center">
              <span class="font-medium text-gray-900">${item.description}</span>
              ${coveredBadge}
            </div>
            <p class="text-xs text-gray-500 mt-0.5">×${item.quantity}</p>
            ${subscriptionSelector}
          </div>
          <div class="text-right ml-4">
            <p class="font-semibold ${priceClass}">${this.formatCurrency(item.total_price)}</p>
            ${isCovered && assignment?.subscriptionData ? `
              <p class="text-xs text-green-600">${assignment.subscriptionData.plan_name}</p>
            ` : ''}
            ${isCovered && assignment?.creditCategory ? `
              <p class="text-xs text-purple-600">Crédit ${this.getCategoryLabel(assignment.creditCategory)}</p>
            ` : ''}
          </div>
        </div>
      </div>
    `
  }

  getCompatibleSubscriptions(item) {
    if (!item.service_type || item.service_type === 'products') {
      return []
    }

    // Bloquer les abonnements pour les bancs solaires si la promo 2ème séance est active
    if (this.hasSecondSessionPromoValue && item.service_type === 'sunbeds') {
      return []
    }

    return this.clientSubscriptionsValue.filter(sub => {
      // Vérifier que le type de service correspond
      if (sub.service_type !== item.service_type) return false
      
      // Pour les soins, vérifier que le treatment_id correspond si spécifié
      if (sub.service_type === 'treatments' && sub.treatment_id) {
        if (item.treatment_id && sub.treatment_id !== item.treatment_id) return false
      }
      
      // Vérifier qu'il reste du solde
      if (sub.mode === 'credit') {
        return this.getRemainingSubscriptionCredit(sub) > 0
      } else {
        return this.getRemainingSubscriptionSessions(sub) > 0
      }
    })
  }

  getRemainingSubscriptionCredit(subscription) {
    const baseCredit = subscription.credit_remaining || 0
    
    // Soustraire ce qui a déjà été assigné à d'autres articles
    const usedInAssignments = Object.values(this.itemPaymentAssignments)
      .filter(a => a.subscriptionId === subscription.id || a.subscriptionId === subscription.id.toString())
      .reduce((sum, a) => sum + (a.amount || 0), 0)
    
    return Math.max(0, baseCredit - usedInAssignments)
  }

  getRemainingSubscriptionSessions(subscription) {
    const baseSessions = subscription.sessions_remaining || 0
    
    // Compter les séances déjà assignées
    const usedSessions = Object.values(this.itemPaymentAssignments)
      .filter(a => a.subscriptionId === subscription.id || a.subscriptionId === subscription.id.toString())
      .length
    
    return Math.max(0, baseSessions - usedSessions)
  }

  // ============================================
  // GESTION DES SÉLECTIONS D'ABONNEMENT
  // ============================================

  onSubscriptionSelect(event) {
    const select = event.target
    const itemId = select.dataset.itemId
    const isChild = select.dataset.isChild === 'true'
    const parentId = select.dataset.parentId
    const subscriptionId = select.value
    
    // Pour les enfants, on utilise l'ID complet (ex: "123_treatment_456")
    // Pour les items normaux, on parse en int
    const itemIdKey = isChild ? itemId : parseInt(itemId)
    
    // Trouver l'item correspondant
    let item
    if (isChild && parentId) {
      const parentItem = this.ticketItemsValue.find(i => i.id === parseInt(parentId))
      if (parentItem && parentItem.children) {
        item = parentItem.children.find(c => c.id === itemId || c.id.toString() === itemId)
      }
    } else {
      item = this.ticketItemsValue.find(i => i.id === parseInt(itemId))
    }
    
    if (!item) return

    // Réinitialiser l'assignation
    this.itemPaymentAssignments[itemId] = {
      subscriptionId: null,
      subscriptionData: null,
      amount: 0,
      covered: false
    }
    
    // Retirer le paiement provisoire si l'abonnement est désélectionné
    this.removePaymentForItem(itemId)

    if (subscriptionId) {
      const subscription = this.clientSubscriptionsValue.find(s => 
        s.id === subscriptionId || s.id.toString() === subscriptionId
      )
      
      if (subscription) {
        const coverableAmount = this.calculateCoverableAmount(subscription, item)
        
        this.itemPaymentAssignments[itemId] = {
          subscriptionId: subscription.id,
          subscriptionData: subscription,
          amount: coverableAmount,
          covered: coverableAmount >= item.total_price
        }

        // Ajouter le paiement provisoire
        this.addSubscriptionPaymentForItem(item, subscription, coverableAmount, isChild, parentId)
      }
    }

    this.renderTicketItems()
    this.updateDisplay()
  }

  onSubscriptionButtonClick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId
    const isChild = button.dataset.isChild === 'true'
    const parentId = button.dataset.parentId
    const subscriptionId = button.dataset.subscriptionId
    
    // Trouver l'item correspondant
    let item
    if (isChild && parentId) {
      const parentItem = this.ticketItemsValue.find(i => i.id === parseInt(parentId))
      if (parentItem && parentItem.children) {
        item = parentItem.children.find(c => c.id === itemId || c.id.toString() === itemId)
      }
    } else {
      item = this.ticketItemsValue.find(i => i.id === parseInt(itemId))
    }
    
    if (!item) return

    // Réinitialiser l'assignation
    this.itemPaymentAssignments[itemId] = {
      subscriptionId: null,
      subscriptionData: null,
      creditCategory: null,
      amount: 0,
      covered: false
    }
    
    // Retirer le paiement provisoire
    this.removePaymentForItem(itemId)

    if (subscriptionId) {
      const subscription = this.clientSubscriptionsValue.find(s => 
        s.id === subscriptionId || s.id.toString() === subscriptionId
      )
      
      if (subscription) {
        const coverableAmount = this.calculateCoverableAmount(subscription, item)
        
        this.itemPaymentAssignments[itemId] = {
          subscriptionId: subscription.id,
          subscriptionData: subscription,
          creditCategory: null,
          amount: coverableAmount,
          covered: coverableAmount >= item.total_price
        }

        // Ajouter le paiement provisoire
        this.addSubscriptionPaymentForItem(item, subscription, coverableAmount, isChild, parentId)
      }
    }

    this.renderTicketItems()
    this.updateDisplay()
  }

  onCashButtonClick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId
    const isChild = button.dataset.isChild === 'true'
    
    // Réinitialiser l'assignation (= paiement comptant)
    this.itemPaymentAssignments[itemId] = {
      subscriptionId: null,
      subscriptionData: null,
      creditCategory: null,
      amount: 0,
      covered: false
    }
    
    // Retirer le paiement provisoire
    this.removePaymentForItem(itemId)

    this.renderTicketItems()
    this.updateDisplay()
  }

  onCreditButtonClick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId
    const isChild = button.dataset.isChild === 'true'
    const parentId = button.dataset.parentId
    const creditCategory = button.dataset.creditCategory
    
    // Trouver l'item correspondant
    let item
    if (isChild && parentId) {
      const parentItem = this.ticketItemsValue.find(i => i.id === parseInt(parentId))
      if (parentItem && parentItem.children) {
        item = parentItem.children.find(c => c.id === itemId || c.id.toString() === itemId)
      }
    } else {
      item = this.ticketItemsValue.find(i => i.id === parseInt(itemId))
    }
    
    if (!item) return

    // Réinitialiser l'assignation
    this.itemPaymentAssignments[itemId] = {
      subscriptionId: null,
      subscriptionData: null,
      creditCategory: null,
      amount: 0,
      covered: false
    }
    
    // Retirer le paiement provisoire
    this.removePaymentForItem(itemId)

    if (creditCategory) {
      const availableCredit = this.getRemainingCreditBalance(creditCategory)
      const coverableAmount = Math.min(availableCredit, item.total_price)
      
      if (coverableAmount > 0) {
        this.itemPaymentAssignments[itemId] = {
          subscriptionId: null,
          subscriptionData: null,
          creditCategory: creditCategory,
          amount: coverableAmount,
          covered: coverableAmount >= item.total_price
        }

        // Ajouter le paiement provisoire
        this.addCreditPaymentForItem(item, creditCategory, coverableAmount, isChild, parentId)
      }
    }

    this.renderTicketItems()
    this.updateDisplay()
  }

  addCreditPaymentForItem(item, category, amount, isChild = false, parentId = null) {
    // Retirer d'abord tout paiement existant pour cet article
    this.removePaymentForItem(item.id)

    const paymentId = `credit_item_${item.id}`
    
    const payment = {
      id: paymentId,
      type: 'credit_wallet',
      label: `Crédit ${this.getCategoryLabel(category)} → ${item.description}`,
      amount: amount,
      data: {
        paymentType: 'credit_wallet',
        walletCategory: category,
        amount: amount,
        forTicketItemId: isChild ? parseInt(parentId) : item.id,
        treatmentId: item.treatment_id || null,
        isChildPayment: isChild
      },
      forItemId: item.id
    }

    this.provisionalPayments.push(payment)
  }

  // Méthode unifiée pour retirer un paiement (abonnement ou crédit)
  removePaymentForItem(itemId) {
    const itemIdStr = String(itemId)
    this.provisionalPayments = this.provisionalPayments.filter(p => String(p.forItemId) !== itemIdStr)
  }

  calculateCoverableAmount(subscription, item) {
    if (subscription.mode === 'credit') {
      const availableCredit = this.getRemainingSubscriptionCredit(subscription)
      return Math.min(availableCredit, item.total_price)
    } else {
      // Mode séances : une séance couvre tout l'article
      return item.total_price
    }
  }

  addSubscriptionPaymentForItem(item, subscription, amount, isChild = false, parentId = null) {
    // Retirer d'abord tout paiement existant pour cet article
    this.removePaymentForItem(item.id)

    const paymentId = `sub_item_${item.id}`
    
    // Pour les enfants (soins décomposés), on utilise le treatment_id du soin
    // Pour les items parents, on ne spécifie pas de treatment_id
    const actualTreatmentId = item.treatment_id || null
    
    let paymentData
    if (subscription.from_ticket) {
      const ticketItemId = parseInt(subscription.id.toString().replace('ticket_', ''))
      paymentData = subscription.mode === 'credit' ? {
        paymentType: 'subscription_credit_from_ticket',
        ticketItemId: ticketItemId,
        amount: amount,
        serviceType: item.service_type,
        forTicketItemId: isChild ? parseInt(parentId) : item.id,
        treatmentId: actualTreatmentId,
        isChildPayment: isChild
      } : {
        paymentType: 'subscription_from_ticket',
        ticketItemId: ticketItemId,
        amount: amount,
        serviceType: item.service_type,
        forTicketItemId: isChild ? parseInt(parentId) : item.id,
        treatmentId: actualTreatmentId,
        isChildPayment: isChild
      }
    } else {
      paymentData = subscription.mode === 'credit' ? {
        paymentType: 'subscription_credit',
        clientSubscriptionId: subscription.id,
        amount: amount,
        serviceType: item.service_type,
        forTicketItemId: isChild ? parseInt(parentId) : item.id,
        treatmentId: actualTreatmentId,
        isChildPayment: isChild
      } : {
        paymentType: 'subscription',
        clientSubscriptionId: subscription.id,
        amount: amount,
        serviceType: item.service_type,
        forTicketItemId: isChild ? parseInt(parentId) : item.id,
        treatmentId: actualTreatmentId,
        isChildPayment: isChild
      }
    }

    const payment = {
      id: paymentId,
      type: subscription.mode === 'credit' ? 'subscription_credit' : 'subscription',
      label: `${subscription.plan_name} → ${item.description}`,
      amount: amount,
      data: paymentData,
      forItemId: item.id
    }

    this.provisionalPayments.push(payment)
  }

  // ============================================
  // RENDU DES CRÉDITS CLIENT (section séparée - désactivée)
  // Les crédits sont maintenant affichés dans les boutons de chaque item
  // ============================================

  renderClientCredits() {
    // Section désactivée - les crédits sont maintenant affichés dans les options de chaque item
    if (!this.hasClientCreditsTarget) return
    this.clientCreditsTarget.innerHTML = ''
  }

  // useCreditWallet - ancienne méthode de la section crédits séparée
  // Conservée pour rétrocompatibilité si la section est réactivée
  useCreditWallet(event) {
    const category = event.currentTarget.dataset.walletCategory
    const balance = parseFloat(event.currentTarget.dataset.walletBalance)
    const remaining = this.getRemainingAmount()
    const amount = Math.min(balance, remaining)

    if (amount <= 0) return

    const payment = {
      id: this.generatePaymentId(),
      type: 'credit_wallet',
      label: `Crédit ${this.getCategoryLabel(category)} - ${this.formatCurrency(amount)}`,
      amount: amount,
      data: {
        paymentType: 'credit_wallet',
        walletCategory: category,
        amount: amount
      }
    }

    this.provisionalPayments.push(payment)
    this.updateDisplay()
  }

  // ============================================
  // PAIEMENTS STANDARDS
  // ============================================

  selectPaymentMethod(event) {
    const button = event.currentTarget
    const methodId = parseInt(button.dataset.methodId)
    const methodName = button.dataset.methodName
    const opensDrawer = button.dataset.opensDrawer === 'true'

    this.selectedPaymentMethod = { id: methodId, name: methodName, opensDrawer }

    // Afficher le formulaire de montant
    if (this.hasAmountFormTarget) {
      this.amountFormTarget.classList.remove('hidden')
    }

    if (this.hasSelectedMethodNameTarget) {
      this.selectedMethodNameTarget.textContent = methodName
    }

    // Afficher/masquer la section rendu monnaie
    if (this.hasCashChangeSectionTarget) {
      if (methodName === 'Espèces') {
        this.cashChangeSectionTarget.classList.remove('hidden')
      } else {
        this.cashChangeSectionTarget.classList.add('hidden')
      }
    }

    // Pré-remplir avec le reste à payer
    const remaining = this.getRemainingAmount()
    if (this.hasAmountInputTarget && remaining > 0) {
      this.amountInputTarget.value = remaining.toFixed(2)
      this.amountInputTarget.focus()
      this.amountInputTarget.select()
      
      // Sync le montant reçu si espèces
      if (methodName === 'Espèces' && this.hasCashReceivedInputTarget) {
        this.cashReceivedInputTarget.value = remaining.toFixed(2)
      }
    }
  }

  cancelPaymentMethod() {
    this.selectedPaymentMethod = null
    
    if (this.hasAmountFormTarget) {
      this.amountFormTarget.classList.add('hidden')
    }
    
    if (this.hasAmountInputTarget) {
      this.amountInputTarget.value = ''
    }
    
    if (this.hasCashReceivedInputTarget) {
      this.cashReceivedInputTarget.value = ''
    }
  }

  fillRemainingAmount(event) {
    event.preventDefault()
    const remaining = this.getRemainingAmount()
    
    if (this.hasAmountInputTarget && remaining > 0) {
      this.amountInputTarget.value = remaining.toFixed(2)
      this.amountInputTarget.focus()
      
      if (this.selectedPaymentMethod?.name === 'Espèces' && this.hasCashReceivedInputTarget) {
        this.cashReceivedInputTarget.value = remaining.toFixed(2)
      }
    }
  }

  onAmountChange() {
    if (this.selectedPaymentMethod?.name === 'Espèces' && this.hasCashReceivedInputTarget && this.hasAmountInputTarget) {
      const amount = parseFloat(this.amountInputTarget.value) || 0
      if (amount > 0) {
        this.cashReceivedInputTarget.value = amount.toFixed(2)
      }
    }
  }

  addStandardPayment(event) {
    if (event) event.preventDefault()

    if (!this.selectedPaymentMethod) {
      alert('Veuillez sélectionner une méthode de paiement')
      return
    }

    const amount = parseFloat(this.amountInputTarget?.value) || 0
    if (amount <= 0) {
      alert('Veuillez saisir un montant valide')
      return
    }

    // Ne pas autoriser un montant supérieur au reste à payer
    const remaining = this.getRemainingAmount()
    if (amount > remaining) {
      alert(`Le montant saisi (${this.formatCurrency(amount)}) dépasse le reste à payer (${this.formatCurrency(remaining)})`)
      return
    }

    // Capturer le rendu monnaie pour espèces
    let cashChange = null
    if (this.selectedPaymentMethod.name === 'Espèces' && this.hasCashReceivedInputTarget) {
      const received = parseFloat(this.cashReceivedInputTarget.value) || 0
      if (received >= amount) {
        cashChange = {
          received: received,
          change: received - amount
        }
      }
    }

    const payment = {
      id: this.generatePaymentId(),
      type: 'standard',
      label: `${this.selectedPaymentMethod.name} - ${this.formatCurrency(amount)}`,
      amount: amount,
      cashChange: cashChange,
      data: {
        paymentType: 'standard',
        paymentMethodId: this.selectedPaymentMethod.id,
        amount: amount
      }
    }

    this.provisionalPayments.push(payment)
    
    // Reset
    this.cancelPaymentMethod()
    this.updateDisplay()
  }

  // ============================================
  // GESTION DES PAIEMENTS
  // ============================================

  removeProvisionalPayment(event) {
    const button = event.target.closest('button')
    const paymentId = button.dataset.paymentId
    
    const payment = this.provisionalPayments.find(p => p.id === paymentId)
    
    // Si c'est un paiement d'abonnement ou crédit lié à un article, réinitialiser l'assignation
    if (payment?.forItemId) {
      this.itemPaymentAssignments[payment.forItemId] = {
        subscriptionId: null,
        subscriptionData: null,
        creditCategory: null,
        amount: 0,
        covered: false
      }
    }
    
    this.provisionalPayments = this.provisionalPayments.filter(p => p.id !== paymentId)
    
    this.renderTicketItems()
    this.updateDisplay()
  }

  // ============================================
  // MISE À JOUR DE L'AFFICHAGE
  // ============================================

  updateDisplay() {
    this.updateAmounts()
    this.updatePaymentsList()
    this.updateValidateButton()
    this.updateStandardPaymentSection()
    this.renderClientCredits()
  }

  updateAmounts() {
    const remaining = this.getRemainingAmount()
    const displayTotal = this.isRefundValue ? Math.abs(this.ticketTotalValue) : this.ticketTotalValue

    if (this.hasTotalAmountTarget) {
      this.totalAmountTarget.textContent = this.formatCurrency(displayTotal)
    }

    if (this.hasRemainingAmountTarget) {
      this.remainingAmountTarget.textContent = this.formatCurrency(remaining)
      
      if (remaining === 0) {
        this.remainingAmountTarget.className = "text-xl font-bold text-green-600"
      } else if (remaining > 0) {
        this.remainingAmountTarget.className = "text-xl font-bold text-red-600"
      } else {
        this.remainingAmountTarget.className = "text-xl font-bold text-orange-600"
      }
    }

    if (this.hasRemainingAmountInlineTarget) {
      this.remainingAmountInlineTarget.textContent = this.formatCurrency(remaining)
    }

    if (this.hasRemainingAmountButtonTarget) {
      this.remainingAmountButtonTarget.textContent = this.formatCurrency(remaining)
    }
  }

  updatePaymentsList() {
    if (!this.hasPaymentsListTarget) return

    let html = ""

    // Filtrer pour n'afficher que les paiements monétaires standards
    // Les paiements par abonnement et crédit restent "attachés" à leur article dans la Zone 1
    const monetaryPayments = this.provisionalPayments.filter(payment => 
      payment.type === 'standard'
    )

    monetaryPayments.forEach(payment => {
      html += `
        <div class="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md mb-2">
          <div class="flex-1">
            <span class="text-sm font-medium text-gray-900">${payment.label}</span>
            ${payment.cashChange ? `
              <span class="block text-green-700 mt-0.5">
                💵 Reçu: ${this.formatCurrency(payment.cashChange.received)} → Rendu: ${this.formatCurrency(payment.cashChange.change)}
              </span>
            ` : ''}
          </div>
          <button type="button" 
                  class="ml-3 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  data-action="click->payment-modal#removeProvisionalPayment"
                  data-payment-id="${payment.id}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      `
    })

    if (html === "") {
      html = this.isRefundValue 
        ? '<p class="text-gray-500 text-sm text-center py-2">Aucun remboursement ajouté</p>'
        : '<p class="text-gray-500 text-sm text-center py-2">Aucun encaissement ajouté</p>'
    }

    this.paymentsListTarget.innerHTML = html

    // Masquer la section si pas de paiements monétaires
    if (this.hasPaymentsSectionTarget) {
      this.paymentsSectionTarget.classList.toggle('hidden', monetaryPayments.length === 0)
    }
  }

  updateValidateButton() {
    const remaining = this.getRemainingAmount()
    const isComplete = Math.abs(remaining) < 0.01

    if (!this.hasValidateButtonTarget) return

    this.validateButtonTarget.disabled = !isComplete

    if (isComplete) {
      this.validateButtonTarget.className = "flex-1 inline-flex justify-center rounded-md border border-transparent bg-green-600 py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-sz-primary-500 focus:ring-offset-2"
      this.validateButtonTarget.textContent = this.isRefundValue ? "✓ Valider le remboursement" : "✓ Valider le paiement"
    } else {
      this.validateButtonTarget.className = "flex-1 inline-flex justify-center rounded-md border border-transparent bg-gray-400 py-3 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sz-primary-500 focus:ring-offset-2 cursor-not-allowed"
      this.validateButtonTarget.textContent = `Reste ${this.formatCurrency(remaining)}`
    }
  }

  updateStandardPaymentSection() {
    const remaining = this.getRemainingAmount()
    
    if (this.hasStandardPaymentSectionTarget) {
      // Désactiver visuellement si tout est payé
      if (remaining <= 0) {
        this.standardPaymentSectionTarget.classList.add('opacity-50', 'pointer-events-none')
      } else {
        this.standardPaymentSectionTarget.classList.remove('opacity-50', 'pointer-events-none')
      }
    }
  }

  // ============================================
  // VALIDATION DU PAIEMENT
  // ============================================

  async validatePayment(event) {
    event.preventDefault()

    // Si le ticket est à 0€, on peut valider sans paiement
    if (this.ticketTotalValue === 0) {
      this.submitSplitPayment([])
      return
    }

    if (this.provisionalPayments.length === 0) {
      alert('Aucun paiement à traiter')
      return
    }

    const remaining = this.getRemainingAmount()
    if (Math.abs(remaining) >= 0.01) {
      alert('Le montant total des paiements ne correspond pas au total du ticket')
      return
    }

    // Préparer le payload pour l'envoi au backend
    const payload = this.provisionalPayments.map(p => p.data)

    await this.submitSplitPayment(payload)
  }

  async submitSplitPayment(payload) {
    try {
      const response = await fetch('/pos/split_payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({
          ticket_id: this.ticketIdValue,
          payment_payload: payload
        })
      })

      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      } else {
        const error = await response.text()
        alert('Erreur lors du traitement du paiement: ' + error)
      }
    } catch (error) {
      console.error('Erreur de réseau:', error)
      alert('Erreur de connexion')
    }
  }

  // ============================================
  // MÉTHODES UTILITAIRES
  // ============================================

  getRemainingAmount() {
    const totalPaid = this.provisionalPayments.reduce((sum, p) => sum + p.amount, 0)
    
    // Pour les tickets d'avoir, on travaille avec le montant absolu
    // Le remaining = montant à rembourser - montant déjà "alloué" au remboursement
    if (this.isRefundValue) {
      const refundAmount = Math.abs(this.ticketTotalValue)
      return refundAmount - totalPaid
    }
    
    return this.ticketTotalValue - totalPaid
  }

  getCategoryLabel(category) {
    const labels = {
      'products': 'Produits',
      'treatments': 'Soins',
      'sunbeds': 'Bancs solaires',
      'general': 'Général'
    }
    return labels[category] || category
  }

  generatePaymentId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9)
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }
}
